import { useContext, useEffect, useState } from 'react';
import { useToast } from '@welbe/web-design-system';

import { generateLinkToUploadCampaignFile } from 'services/services';

import { Communication, Campaign } from 'types';
import { renderHour, renderISODate } from 'utils';
import { CampaignContext } from 'Views/Campaigns/components/Campaign/Campaign.context';

import {
	getTypeCommunication,
	uploadCommunicationFile,
	postCommunicationService,
	putCommunicationService,
	deleteCommunication,
} from '../../services/Communication';
import { PreviewEmailType } from 'Views/Campaigns/constants/previewEmailType';
import { PreviewEmailTypeEnum } from 'Views/Campaigns/enum/previewEmailType.enum';
import Analytics from 'helpers/analytics';
import { loggerInfo } from 'utils/infoLog';

type useModalNewCommunicationProps = {
	onClose: () => void;
	campaign: Campaign;
	communication?: Communication;
	initialCommunicationType?: any;
};

type SelectProps = {
	id: string;
	value: string;
};

type CommunicationFormType = {
	id: string;
	communicationType: { id: string; value: string };
	previewType: { id: PreviewEmailTypeEnum; value: string };
	name: string;
	title: string;
	message: string;
	date: string;
	time: { id: string; value: string };
	fileName: string;
};

const clearState = {
	id: '',
	value: 'Seleciona una opción',
};

export const useModalCommunication = ({
	onClose,
	campaign,
	communication,
	initialCommunicationType,
}: useModalNewCommunicationProps) => {
	const { toast } = useToast();
	const campaignId = window.location.pathname.split('/campaign/').pop() || '';
	const { handleSetReloadCommunication } = useContext(CampaignContext);

	const [isLoading, setLoading] = useState(false);

	const [isLoadingCommunicationType, setLoadingCommunicationType] =
		useState(false);
	const [communicationTypeOptions, setCommunicationTypeOptions] = useState<
		SelectProps[]
	>([]);

	const [
		showModalPreviewEmailCommunication,
		setShowModalPreviewEmailCommunication,
	] = useState(false);

	const [communicationForm, setCommunicationForm] =
		useState<CommunicationFormType>(
			communication?.id
				? {
						id: communication.id,
						communicationType: {
							id: communication?.communicationType?.id,
							value: communication?.communicationType?.name,
						},
						previewType: {
							id: communication?.useCustomTemplate
								? PreviewEmailTypeEnum.CUSTOM
								: PreviewEmailTypeEnum.DEFAULT,
							value: communication?.useCustomTemplate
								? PreviewEmailType[PreviewEmailTypeEnum.CUSTOM]
								: PreviewEmailType[PreviewEmailTypeEnum.DEFAULT],
						},
						name: communication.name,
						title: communication.messageTitle,
						message: communication.useCustomTemplate
							? communication.messageBody
							: '',
						date: communication.sendingDateTime.split('T')[0],
						time: {
							id: renderHour(communication.sendingDateTime) || '',
							value: renderHour(communication.sendingDateTime) || '',
						},
						fileName:
							communication?.fileName?.replace(
								'campaign/communications/',
								'',
							) || '',
				  }
				: {
						id: '',
						communicationType: clearState,
						previewType: {
							id: PreviewEmailTypeEnum.DEFAULT,
							value: PreviewEmailType[PreviewEmailTypeEnum.DEFAULT],
						},
						name: '',
						title: '',
						message: '',
						date: '',
						time: clearState,
						fileName: '',
				  },
		);

	const [file, setFile] = useState<File | null>(null);

	const [errorForm, setErrorForm] = useState({
		communicationType: '',
		name: '',
		title: '',
		message: '',
		date: '',
		time: '',
	});

	const [showModalConfirm, setShowModalConfirm] = useState(false);

	const validationStep = (): boolean => {
		let isValid = true;
		setErrorForm({
			...errorForm,
			name: !communicationForm.name ? 'Nombre obligatorio' : '',
			communicationType:
				communicationForm.communicationType.id === ''
					? 'Selecciona un tipo de comunicación'
					: '',
			title: !communicationForm.title ? 'Título obligatorio' : '',
			message:
				!communicationForm.message &&
				communicationForm?.previewType.id !== PreviewEmailTypeEnum.DEFAULT
					? 'Mensaje obligatorio'
					: '',
			date: !communicationForm.date ? 'Fecha obligatoria' : '',
			time:
				!communicationForm.time || communicationForm.time.id === ''
					? 'Hora obligatoria'
					: '',
		});
		if (
			!communicationForm.name ||
			communicationForm.communicationType.id === '' ||
			!communicationForm.title ||
			(!communicationForm.message &&
				communicationForm?.previewType.id !== PreviewEmailTypeEnum.DEFAULT) ||
			!communicationForm.date ||
			!communicationForm.time ||
			communicationForm.time.id === ''
		) {
			isValid = false;
		}
		return isValid;
	};

	const handleGenericErrorUploadFile = () => {
		toast.error('No se pudo hacer el upload.');
	};

	const requestUploadUrl = async () => {
		const removeSpecialCaracter = (regexValue: string) =>
			regexValue.replace(/[^0-9a-zA-Z.]/g, '_').toLowerCase();
		try {
			const fileName: any = file?.name || '';
			const fileNameFormatted = removeSpecialCaracter(fileName);

			const { data } = await generateLinkToUploadCampaignFile(
				fileNameFormatted,
			);

			return {
				urlRequest: data.content.url,
				fileName: data.content.filename,
				file,
			};
		} catch (error) {
			loggerInfo({
				data: error,
				context: 'campaign',
				location: 'requestUploadUrl',
			});
			handleGenericErrorUploadFile();
		}
	};

	const uploadFile = async (data: any) => {
		try {
			await uploadCommunicationFile(data);
		} catch (error) {
			handleGenericErrorUploadFile();
		}
	};

	const getFileName = (fileName?: string) => {
		if (fileName) return fileName;
		if (communicationForm?.fileName) return communicationForm.fileName;
		return '';
	};

	const sendCommunication = async (fileName?: string) => {
		const payload = {
			campaignId: campaign.id,
			name: communicationForm.name,
			communicationTypeId: communicationForm.communicationType.id,
			useCustomTemplate:
				communicationForm.previewType.id === PreviewEmailTypeEnum.DEFAULT
					? false
					: true,
			messageTitle: communicationForm.title,
			messageBody:
				communicationForm?.previewType.id === PreviewEmailTypeEnum.DEFAULT
					? ''
					: communicationForm.message,
			messageFile: getFileName(fileName),
			sendingDate: renderISODate(communicationForm.date),
			sendingTime: communicationForm.time.id,
		};

		!communication?.id
			? postCommunicationService(payload)
			: putCommunicationService({
					...payload,
					communicationId: communication?.id,
			  });
	};

	const handleConfirm = async () => {
		if (validationStep()) {
			setLoading(true);
			try {
				if (file?.name) {
					const data = await requestUploadUrl();
					if (data) {
						await uploadFile(data);
						await sendCommunication(data.fileName);
						Analytics.track('campaign', 'save-add-communication-modal', data);
						setShowModalConfirm(true);
					} else {
						throw 'Invalid URL';
					}
				} else {
					await sendCommunication();
					setShowModalConfirm(true);
				}
			} catch ({ response }) {
				toast.error(response.data.message);
			} finally {
				setLoading(false);
			}
		}
	};

	const handleDeleteCommunication = async () => {
		setLoading(true);
		try {
			await deleteCommunication({
				campaignId: campaign.id,
				communicationId: communication ? communication.id : '',
			});
			setLoading(false);
			handleSetReloadCommunication();
			onClose();
		} catch ({ response }) {
			toast.error(response.data.message);
		} finally {
			setLoading(false);
		}
	};

	const handleSeleteCommunicationType = (
		selectedCommunicationType: SelectProps,
	) => {
		setCommunicationForm({
			...communicationForm,
			communicationType: selectedCommunicationType,
		});
		setErrorForm({
			...errorForm,
			communicationType: '',
		});
	};

	const handleNameCommunication = (nameCommunication: string) => {
		setCommunicationForm({
			...communicationForm,
			name: nameCommunication,
		});
		setErrorForm({
			...errorForm,
			name: '',
		});
	};

	const handleTitleCommunication = (titleCommunication: string) => {
		setCommunicationForm({
			...communicationForm,
			title: titleCommunication,
		});
		setErrorForm({
			...errorForm,
			title: '',
		});
	};

	const handleMessageCommunication = (messageCommunication: string) => {
		setCommunicationForm({
			...communicationForm,
			message: messageCommunication,
		});
		setErrorForm({
			...errorForm,
			message: '',
		});
	};

	const handleFileCommunication = (e: any) => {
		setFile(e);
		setCommunicationForm({
			...communicationForm,
			fileName: e.name || '',
		});
	};

	const handleDateCommunication = (dateCommunication: any) => {
		communicationForm.date = dateCommunication;
		errorForm.date = '';
	};

	const handleTimeCommunication = (timeCommunication: SelectProps) => {
		setCommunicationForm({
			...communicationForm,
			time: timeCommunication,
		});
		setErrorForm({
			...errorForm,
			time: '',
		});
	};

	const handleChangePreviewType = (type: any) => {
		setCommunicationForm({
			...communicationForm,
			previewType: type,
			message: '',
		});
	};

	const handleCloseModalConfirm = () => {
		handleSetReloadCommunication();
		setShowModalConfirm(false);
		onClose();
	};

	const handlePreviewEmailCommunication = () => {
		Analytics.track('campaign', 'preview-add-communication-modal');
		setShowModalPreviewEmailCommunication(true);
	};

	const handleClosePreviewEmailCommunication = () => {
		setShowModalPreviewEmailCommunication(false);
	};

	const fetchTypeCommunication = async () => {
		setLoadingCommunicationType(true);
		try {
			const { data } = await getTypeCommunication(campaignId);
			setCommunicationTypeOptions(
				data?.content.communicationTypes.map((type: any) => {
					return { id: type.id, value: type.name };
				}),
			);
		} catch (error) {
			console.error(error);
		} finally {
			setLoadingCommunicationType(false);
		}
	};

	const createHeightModalResponsive = (): string => {
		if (window.matchMedia('(max-height: 700px)').matches) return '600px';
		if (window.matchMedia('(max-height: 800px)').matches) return '700px';
		if (window.matchMedia('(max-height: 900px)').matches) return '730px';
		if (window.matchMedia('(max-height: 1400px)').matches) return '850px';

		return '';
	};

	const verifyPermissionToDeleteCommunication = (): boolean => {
		if (communication?.id) {
			if (
				communication?.communicationType.id.indexOf('evaluation') != -1 ||
				communication?.communicationType.id.indexOf('closing') != -1
			)
				return true;
		}
		return false;
	};

	useEffect(() => {
		fetchTypeCommunication();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setErrorForm((prev) => {
			return {
				...prev,
				message: '',
			};
		});
	}, [communicationForm.previewType]);

	useEffect(() => {
		initialCommunicationType?.id &&
			handleSeleteCommunicationType(initialCommunicationType);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialCommunicationType]);

	return {
		isLoading,
		isLoadingCommunicationType,
		communicationTypeOptions,
		communicationForm,
		errorForm,
		showModalConfirm,
		file,
		showModalPreviewEmailCommunication,
		createHeightModalResponsive,
		verifyPermissionToDeleteCommunication,
		handleConfirm,
		handleSeleteCommunicationType,
		handleNameCommunication,
		handleTitleCommunication,
		handleMessageCommunication,
		handleFileCommunication,
		handleDateCommunication,
		handleTimeCommunication,
		handleCloseModalConfirm,
		handleDeleteCommunication,
		handlePreviewEmailCommunication,
		handleClosePreviewEmailCommunication,
		handleChangePreviewType,
	};
};
